/**
 * MOCKUP D — Hybrid Editorial (textures variées par section)
 *  - HERO : photo plein cadre avec voile + texte blanc (style magazine cover)
 *  - LOSS : fond noir mat profond (le SEUL dark, justifié pour effet alerte)
 *  - MAP  : papier ancien / texture lin beige, encre sépia
 * Chaque section a sa propre "matière" pour casser la monotonie.
 */
import { Link } from 'react-router-dom';
import { Lock, ArrowRight, MessageCircle } from 'lucide-react';

const D = {
  cream: '#F4EFE5',
  paperLight: '#EFE8D6',
  paperDark: '#1A1612',
  ink: '#1A1714',
  inkBody: '#3D362C',
  inkMute: '#7A6F5F',
  gold: '#A87C4F',
  brand: '#FF6B35',
  terracotta: '#C8553D',
};

const fontDisplay = 'SF Pro Display, -apple-system, system-ui, sans-serif';
const fontSerif = 'Georgia, "Times New Roman", serif';
const fontMono = 'SF Mono, ui-monospace, monospace';

// Subtle grain SVG (used as texture overlay)
const grainSvg = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.35 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>";

function Shell({ children }) {
  return (
    <div
      className="min-h-screen"
      style={{ background: D.cream, color: D.ink, fontFamily: fontDisplay, paddingTop: 60 }}
    >
      <div className="mx-auto" style={{ maxWidth: 414 }}>
        {children}
      </div>
    </div>
  );
}

// ─── HERO — Magazine cover with full photo ─────────────────────────
function HeroD() {
  return (
    <div className="px-4 mb-6">
      <div
        className="relative overflow-hidden"
        style={{ borderRadius: 4, height: 540, border: `0.5px solid rgba(26,23,20,0.10)` }}
      >
        {/* Background photo */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/o5mmhfxe_IMG_0410.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'saturate(1.05) contrast(1.02)',
          }}
        />
        {/* Grain texture */}
        <div className="absolute inset-0" style={{ backgroundImage: `url("${grainSvg}")`, opacity: 0.20, mixBlendMode: 'multiply' }} />
        {/* Gradient veil */}
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.30) 35%, rgba(0,0,0,0.92) 100%)' }}
        />

        {/* Top corner — issue / numéro */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-start justify-between">
          <div>
            <p style={{ fontFamily: fontMono, fontSize: 8.5, color: 'rgba(255,255,255,0.85)', letterSpacing: '0.30em', textTransform: 'uppercase', fontWeight: 700 }}>
              No. 01 · Édition 2026
            </p>
            <p style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>
              Le journal des marques fières
            </p>
          </div>
          <span
            className="inline-flex items-center gap-1"
            style={{
              background: 'rgba(0,0,0,0.45)',
              border: '0.5px solid rgba(168,124,79,0.50)',
              color: D.gold,
              borderRadius: 4,
              padding: '5px 9px',
              fontFamily: fontMono,
              fontSize: 8,
              fontWeight: 800,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              backdropFilter: 'blur(8px)',
            }}
          >
            <Lock className="w-2.5 h-2.5" strokeWidth={2.5} />
            3 places · sur sélection
          </span>
        </div>

        {/* Bottom title block */}
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <p style={{ fontFamily: fontMono, fontSize: 9, color: D.gold, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 800, marginBottom: 10 }}>
            Salon Privé · Direction de marque
          </p>
          <h1
            style={{
              fontFamily: fontSerif,
              fontSize: 60,
              fontWeight: 700,
              color: '#FFFFFF',
              letterSpacing: '-0.04em',
              lineHeight: 0.92,
              marginBottom: 14,
            }}
          >
            Vous avez<br/>le resto.<br/>
            <span style={{ fontStyle: 'italic', color: D.gold }}>Je donne la marque.</span>
          </h1>

          <div className="flex items-center gap-2 mb-3" style={{ borderTop: '0.5px solid rgba(255,255,255,0.25)', paddingTop: 10 }}>
            <img
              src="https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/0fmy06l5_avatar-sabril.png"
              alt="Sabril"
              style={{ width: 32, height: 32, borderRadius: 999, border: '0.5px solid rgba(255,255,255,0.40)' }}
            />
            <div>
              <p style={{ fontFamily: fontDisplay, fontSize: 11, color: '#FFFFFF', fontWeight: 700, letterSpacing: '-0.01em' }}>
                Sabril Jurczeszyn
              </p>
              <p style={{ fontFamily: fontMono, fontSize: 8.5, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.14em', textTransform: 'uppercase' }}>
                Founder · 2018 → ∞
              </p>
            </div>
          </div>

          <p style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.45, marginBottom: 14 }}>
            Cercle privé des restaurateurs qui transforment leur resto en une marque dont ils sont fiers. Rentable. Transmissible.
          </p>

          <button
            style={{
              width: '100%',
              background: '#FFFFFF',
              color: D.ink,
              borderRadius: 999,
              padding: '12px 16px',
              fontFamily: fontDisplay,
              fontSize: 13.5,
              fontWeight: 700,
              letterSpacing: '-0.01em',
            }}
          >
            Candidater au Salon →
          </button>
        </div>
      </div>

      {/* KPI ribbon below the cover */}
      <div className="grid grid-cols-3 gap-0 mt-5" style={{ borderTop: `0.5px solid ${D.ink}25`, borderBottom: `0.5px solid ${D.ink}25` }}>
        {[
          { v: '6',   l: 'Restos accompagnés', tone: D.brand },
          { v: '4',   l: 'Villes · 4 pays',    tone: D.gold },
          { v: '12h', l: 'Coaching inclus',    tone: '#1FA644' },
        ].map((k, i) => (
          <div key={k.l} className="text-center" style={{ padding: '14px 6px', borderLeft: i === 0 ? 'none' : `0.5px solid ${D.ink}15` }}>
            <p style={{ fontFamily: fontSerif, fontSize: 30, fontWeight: 700, color: k.tone, letterSpacing: '-0.02em', lineHeight: 1 }}>
              {k.v}
            </p>
            <p style={{ fontFamily: fontMono, fontSize: 7.5, color: D.inkMute, letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 6 }}>
              {k.l}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── LOSS — Le SEUL dark, justifié comme "alerte" ──────────────────
function LossD() {
  return (
    <div className="px-4 mb-6">
      <div className="flex items-baseline gap-2 mb-3">
        <span style={{ fontFamily: fontMono, fontSize: 9, color: D.brand, letterSpacing: '0.20em', textTransform: 'uppercase', fontWeight: 800 }}>
          §2 · La réalité
        </span>
        <span style={{ flex: 1, height: 0.5, background: `${D.ink}20` }} />
      </div>
      <h2 style={{ fontFamily: fontSerif, fontSize: 36, fontWeight: 700, color: D.ink, letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 6 }}>
        Combien tu perds<br/><span style={{ fontStyle: 'italic', color: D.brand }}>chaque mois.</span>
      </h2>
      <p style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 12, color: D.inkMute, marginBottom: 16 }}>
        Calculateur · 30 secondes pour mesurer la fuite.
      </p>

      {/* DARK card (justified) */}
      <div
        className="relative overflow-hidden"
        style={{
          background: D.paperDark,
          borderRadius: 6,
          border: `0.5px solid ${D.ink}`,
          padding: '20px 18px 16px',
          boxShadow: '0 18px 36px -16px rgba(26,23,20,0.45)',
        }}
      >
        {/* grain on dark */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: `url("${grainSvg}")`, opacity: 0.30, mixBlendMode: 'overlay' }} />

        <div className="relative">
          {/* Slider 1 */}
          <div className="mb-4">
            <div className="flex items-baseline justify-between mb-2">
              <span style={{ fontFamily: fontMono, fontSize: 9.5, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
                CA mensuel
              </span>
              <span style={{ fontFamily: fontSerif, fontSize: 22, fontWeight: 700, color: D.gold, letterSpacing: '-0.02em' }}>
                60 K€
              </span>
            </div>
            <div className="relative h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.10)' }}>
              <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: '36%', background: D.gold }} />
              <div className="absolute" style={{ left: '36%', top: '-6.5px', width: 14, height: 14, borderRadius: 999, background: D.gold, boxShadow: `0 0 0 4px ${D.gold}30`, marginLeft: -7 }} />
            </div>
          </div>

          {/* Slider 2 */}
          <div className="mb-5">
            <div className="flex items-baseline justify-between mb-2">
              <span style={{ fontFamily: fontMono, fontSize: 9.5, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.18em', textTransform: 'uppercase' }}>
                Food cost actuel
              </span>
              <span style={{ fontFamily: fontSerif, fontSize: 22, fontWeight: 700, color: D.brand, letterSpacing: '-0.02em' }}>
                38 %
              </span>
            </div>
            <div className="relative h-1 rounded-full" style={{ background: 'rgba(255,255,255,0.10)' }}>
              <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: '65%', background: D.brand }} />
              <div className="absolute" style={{ left: '65%', top: '-6.5px', width: 14, height: 14, borderRadius: 999, background: D.brand, boxShadow: `0 0 0 4px ${D.brand}30`, marginLeft: -7 }} />
            </div>
            <p style={{ fontFamily: fontMono, fontSize: 8, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 6, textAlign: 'center' }}>
              Cible saine du métier · 27%
            </p>
          </div>

          {/* Result — magazine-style display number */}
          <div style={{ borderTop: '0.5px solid rgba(255,255,255,0.15)', paddingTop: 14 }}>
            <p style={{ fontFamily: fontMono, fontSize: 9, color: D.brand, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 800 }}>
              Tu laisses chaque mois
            </p>
            <p style={{ fontFamily: fontSerif, fontSize: 64, fontWeight: 700, color: D.brand, letterSpacing: '-0.045em', lineHeight: 1, marginTop: 6 }}>
              6 600
              <span style={{ fontSize: 32, fontWeight: 500, color: 'rgba(255,107,53,0.65)', marginLeft: 6 }}>€</span>
            </p>
            <p style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 12, color: 'rgba(255,255,255,0.75)', marginTop: 10, lineHeight: 1.4 }}>
              Soit{' '}
              <span style={{ color: '#FFFFFF', fontWeight: 700, fontFamily: fontDisplay, fontStyle: 'normal' }}>
                79 200 €
              </span>{' '}
              par an qui partent en fumée — sans rien faire d&apos;autre.
            </p>
          </div>

          {/* Récupérable banner */}
          <div
            style={{
              background: 'rgba(31,166,68,0.10)',
              border: '0.5px solid rgba(31,166,68,0.30)',
              borderRadius: 4,
              padding: '10px 12px',
              marginTop: 14,
            }}
          >
            <p style={{ fontFamily: fontMono, fontSize: 8.5, color: '#54D77A', letterSpacing: '0.18em', textTransform: 'uppercase', fontWeight: 800 }}>
              Récupérable avec le programme
            </p>
            <p style={{ fontFamily: fontSerif, fontSize: 22, fontWeight: 700, color: '#54D77A', letterSpacing: '-0.02em', marginTop: 2 }}>
              +6 600 € <span style={{ fontSize: 12, opacity: 0.7 }}>/mois</span>
            </p>
          </div>

          <button
            style={{
              width: '100%',
              background: '#FFFFFF',
              color: D.ink,
              borderRadius: 999,
              padding: '12px 16px',
              fontFamily: fontDisplay,
              fontSize: 13,
              fontWeight: 700,
              marginTop: 14,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
          >
            <MessageCircle className="w-4 h-4" />
            En parler à Sabril maintenant
          </button>
        </div>
      </div>

      <p className="mt-2 px-1" style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 10, color: D.inkMute }}>
        ¹ Calcul indicatif basé sur l&apos;écart food cost vs cible saine du métier.
      </p>
    </div>
  );
}

// ─── MAP — Paper / Linen texture, sepia ink ─────────────────────────
function MapD() {
  return (
    <div className="px-4 mb-8">
      <div className="flex items-baseline gap-2 mb-3">
        <span style={{ fontFamily: fontMono, fontSize: 9, color: D.gold, letterSpacing: '0.20em', textTransform: 'uppercase', fontWeight: 800 }}>
          §3 · Le terrain
        </span>
        <span style={{ flex: 1, height: 0.5, background: `${D.ink}20` }} />
      </div>
      <h2 style={{ fontFamily: fontSerif, fontSize: 36, fontWeight: 700, color: D.ink, letterSpacing: '-0.03em', lineHeight: 1, marginBottom: 6 }}>
        Là où <span style={{ fontStyle: 'italic', color: D.gold }}>je travaille.</span>
      </h2>
      <p style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 13, color: D.inkBody, marginBottom: 16, lineHeight: 1.5 }}>
        De Cannes à Miami — un circuit où je construis des marques rentables.{' '}
        <span style={{ color: D.ink, fontWeight: 600, fontStyle: 'normal', fontFamily: fontDisplay }}>10 restos accompagnés · 3 places restantes.</span>
      </p>

      {/* Paper-textured map */}
      <div
        className="relative overflow-hidden"
        style={{
          background: D.paperLight,
          borderRadius: 6,
          border: `0.5px solid ${D.gold}40`,
          padding: '18px 16px 14px',
          boxShadow: '0 6px 14px -8px rgba(26,23,20,0.18), inset 0 0 0 0.5px rgba(168,124,79,0.20)',
        }}
      >
        {/* Paper grain */}
        <div className="absolute inset-0 pointer-events-none" style={{ backgroundImage: `url("${grainSvg}")`, opacity: 0.40, mixBlendMode: 'multiply' }} />

        {/* Decorative corner ornaments */}
        {[['top:8;left:8','M0,0 L20,0 M0,0 L0,20'], ['top:8;right:8','M20,0 L0,0 M20,0 L20,20'], ['bottom:8;left:8','M0,20 L20,20 M0,20 L0,0'], ['bottom:8;right:8','M20,20 L0,20 M20,20 L20,0']].map(([pos, d], i) => (
          <svg key={i} className="absolute" style={Object.fromEntries(pos.split(';').map(p => p.split(':')))} width="20" height="20" viewBox="0 0 20 20">
            <path d={d} stroke={D.gold} strokeWidth="0.8" fill="none" />
          </svg>
        ))}

        <div className="relative">
          <p className="text-center" style={{ fontFamily: fontMono, fontSize: 8.5, color: D.gold, letterSpacing: '0.30em', textTransform: 'uppercase', fontWeight: 800, marginBottom: 10 }}>
            ✦ Cartographie ✦
          </p>

          {/* Map area */}
          <div className="relative" style={{ height: 220, overflow: 'hidden' }}>
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 380 220" preserveAspectRatio="none">
              {/* meridians/parallels in sepia ink */}
              <g stroke={D.gold} strokeWidth="0.4" fill="none" opacity="0.40">
                <ellipse cx="190" cy="110" rx="170" ry="100" />
                <ellipse cx="190" cy="110" rx="120" ry="70" />
                <ellipse cx="190" cy="110" rx="60"  ry="35" />
                <line x1="20" y1="110" x2="360" y2="110" />
                <line x1="190" y1="10" x2="190" y2="210" />
              </g>
              {/* connections (dashed) */}
              <path d="M 90 130 Q 160 80 230 80" stroke={D.gold} strokeWidth="1" fill="none" strokeDasharray="2 3" />
              <path d="M 230 80 L 290 120" stroke={D.gold} strokeWidth="1" fill="none" strokeDasharray="2 3" />
              <path d="M 230 80 Q 200 150 180 170" stroke={D.gold} strokeWidth="1" fill="none" strokeDasharray="2 3" />
            </svg>

            {[
              { x: 90,  y: 130, name: 'Miami',     country: 'États-Unis', count: '1/3', tone: '#1FA644' },
              { x: 230, y: 80,  name: 'Marbella',  country: 'Espagne',    count: '2/2', tone: D.terracotta },
              { x: 290, y: 120, name: 'Cannes',    country: 'France',     count: '1/2', tone: '#1FA644' },
              { x: 180, y: 170, name: 'Marrakech', country: 'Maroc',      count: '6/6', tone: D.terracotta },
            ].map((c) => (
              <div key={c.name} className="absolute" style={{ left: c.x, top: c.y, transform: 'translate(-50%, -50%)' }}>
                <span style={{ display: 'block', width: 8, height: 8, borderRadius: 999, background: c.tone, boxShadow: `0 0 0 4px ${c.tone}30` }} />
                <div className="absolute" style={{ left: 12, top: -8, whiteSpace: 'nowrap' }}>
                  <p style={{ fontFamily: fontSerif, fontSize: 12, fontWeight: 700, color: D.ink, lineHeight: 1, fontStyle: 'italic' }}>{c.name}</p>
                  <p style={{ fontFamily: fontMono, fontSize: 7, color: D.inkMute, letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 2 }}>{c.country}</p>
                  <span style={{ display: 'inline-block', marginTop: 2, color: c.tone, fontFamily: fontMono, fontSize: 8, fontWeight: 800, letterSpacing: '0.12em', borderBottom: `1px solid ${c.tone}` }}>
                    {c.count} restos
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* footer */}
          <div className="flex items-center justify-between mt-3 px-1" style={{ borderTop: `0.5px solid ${D.gold}40`, paddingTop: 8 }}>
            <span style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 11, color: D.terracotta, fontWeight: 700 }}>
              10 / 13 places prises
            </span>
            <span style={{ fontFamily: fontMono, fontSize: 8, color: D.inkMute, letterSpacing: '0.16em', textTransform: 'uppercase' }}>
              4 villes · 13 slots
            </span>
          </div>
        </div>
      </div>

      <p className="mt-2 text-center" style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 10, color: D.inkMute }}>
        Carte stylisée · proportions non strictes.
      </p>
    </div>
  );
}

export default function MockupD() {
  return (
    <Shell>
      <div className="px-4 pt-4 pb-2 flex items-center justify-between">
        <span style={{ fontFamily: fontMono, fontSize: 9.5, color: D.gold, letterSpacing: '0.20em', textTransform: 'uppercase', fontWeight: 800 }}>
          Direction D · Hybride Éditorial
        </span>
        <Link to="/prsnl" style={{ fontFamily: fontDisplay, fontSize: 11, color: D.inkMute, fontWeight: 600 }}>
          ← Retour
        </Link>
      </div>

      <HeroD />
      <LossD />
      <MapD />

      <p className="px-4 pb-8 text-center" style={{ fontFamily: fontSerif, fontStyle: 'italic', fontSize: 11.5, color: D.inkMute }}>
        Aperçu Direction D — Magazine éditorial · texture variable par section
      </p>
    </Shell>
  );
}
